import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  ExecutionChatResultCard,
  ExecutionChatResultCardAction,
  ExecutionChatResultCardArtifact,
  ExecutionChatResultCardTone,
} from '@shared/types/execution-chat';

const TONE_STYLES: Record<
  ExecutionChatResultCardTone,
  {
    text: string;
    dot: string;
  }
> = {
  positive: {
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
  },
  warning: {
    text: 'text-amber-700',
    dot: 'bg-amber-500',
  },
  critical: {
    text: 'text-red-700',
    dot: 'bg-red-500',
  },
  neutral: {
    text: 'text-gray-600',
    dot: 'bg-gray-400',
  },
};

const ACTION_STYLES: Record<
  NonNullable<ExecutionChatResultCardAction['variant']>,
  string
> = {
  primary: 'border-red-500 bg-red-500 text-white hover:bg-red-600',
  secondary:
    'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50',
  ghost: 'border-transparent bg-transparent text-red-500 hover:bg-red-50',
};

const ARTIFACT_KIND_LABELS: Record<string, string> = {
  crm_note: 'Note CRM',
  email_draft: 'Email',
  gmail_draft: 'Brouillon Gmail',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isArtifact = (value: unknown): value is ExecutionChatResultCardArtifact =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.label === 'string' &&
  typeof value.kind === 'string';

const isAction = (value: unknown): value is ExecutionChatResultCardAction =>
  isRecord(value) && typeof value.id === 'string' && typeof value.label === 'string';

export const isExecutionChatResultCard = (
  value: unknown
): value is ExecutionChatResultCard =>
  isRecord(value) &&
  typeof value.executionId === 'string' &&
  typeof value.title === 'string' &&
  typeof value.subtitle === 'string' &&
  typeof value.statusLabel === 'string' &&
  (value.statusTone === 'positive' ||
    value.statusTone === 'warning' ||
    value.statusTone === 'critical' ||
    value.statusTone === 'neutral') &&
  (value.quality === 'valid' ||
    value.quality === 'partial' ||
    value.quality === 'broken') &&
  typeof value.summary === 'string' &&
  Array.isArray(value.artifacts) &&
  value.artifacts.every(isArtifact) &&
  Array.isArray(value.actions) &&
  value.actions.every(isAction) &&
  typeof value.detailHref === 'string';

const ResultAction = ({ action }: { action: ExecutionChatResultCardAction }) => {
  const variant = action.variant ?? 'secondary';
  const className = cn(
    'inline-flex min-h-9 items-center justify-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-medium transition',
    ACTION_STYLES[variant],
    action.disabled && 'cursor-not-allowed opacity-45 hover:bg-transparent'
  );

  if (action.disabled || !action.href) {
    return (
      <button type="button" className={className} disabled title={action.disabledReason}>
        {action.label}
      </button>
    );
  }

  if (action.external) {
    return (
      <a href={action.href} target="_blank" rel="noreferrer" className={className}>
        {action.label}
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    );
  }

  return (
    <Link to={action.href} className={className}>
      {action.label}
      {variant === 'ghost' && <ArrowRight className="h-3.5 w-3.5" />}
    </Link>
  );
};

export const ExecutionChatResultCardView = ({
  card,
}: {
  card: ExecutionChatResultCard;
}) => {
  const tone = TONE_STYLES[card.statusTone];
  const primaryActions = card.actions.filter((action) => action.variant === 'primary');
  const secondaryActions = card.actions.filter((action) => action.variant !== 'primary');

  return (
    <article className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-4 py-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-xl font-semibold tracking-tight text-gray-950">
              {card.title}
            </h3>
            <span
              className={cn(
                'inline-flex items-center text-xs font-medium',
                tone.text
              )}
            >
              <span className={cn('mr-1.5 h-1.5 w-1.5 rounded-full', tone.dot)} />
              {card.statusLabel}
            </span>
          </div>
          <p className="mt-1 text-sm leading-6 text-gray-600">{card.subtitle}</p>
        </div>

        {card.score && (
          <div className="shrink-0 text-right">
            <p className="text-3xl font-semibold tracking-tight text-gray-950">
              {card.score.value}
              <span className="text-base font-medium text-gray-500">
                /{card.score.max}
              </span>
            </p>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
              {card.score.label}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-4 px-4 py-4">
        <p className="text-sm leading-6 text-gray-800">{card.summary}</p>

        {card.metrics.length > 0 && (
          <div className="grid gap-2 sm:grid-cols-2">
            {card.metrics.map((metric) => (
              <div
                key={metric.id}
                className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
                  {metric.label}
                </p>
                <p className="mt-1 text-sm font-medium text-gray-950">{metric.value}</p>
              </div>
            ))}
          </div>
        )}

        {card.artifacts.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
              Livrables
            </p>
            <div className="mt-2 space-y-2">
              {card.artifacts.map((artifact) => (
                <div key={artifact.id} className="flex gap-2 text-sm">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-950">
                      {artifact.href ? (
                        artifact.external ? (
                          <a
                            href={artifact.href}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:underline"
                          >
                            {artifact.label}
                          </a>
                        ) : (
                          <Link to={artifact.href} className="hover:underline">
                            {artifact.label}
                          </Link>
                        )
                      ) : (
                        artifact.label
                      )}
                      {ARTIFACT_KIND_LABELS[artifact.kind] && (
                        <span className="ml-1.5 font-normal text-gray-500">
                          · {ARTIFACT_KIND_LABELS[artifact.kind]}
                        </span>
                      )}
                    </p>
                    {artifact.detail && (
                      <p className="truncate text-xs text-gray-500">{artifact.detail}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {typeof card.evidenceCount === 'number' && card.evidenceCount > 0 && (
          <p className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500">
            Preuves utilisées · {card.evidenceCount}
          </p>
        )}
      </div>

      <div className="border-t border-gray-200 bg-gray-50 px-4 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
          Et maintenant ?
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {primaryActions.map((action) => (
            <ResultAction key={action.id} action={action} />
          ))}
          {secondaryActions.map((action) => (
            <ResultAction key={action.id} action={action} />
          ))}
        </div>
        {card.activityLabel && (
          <p className="mt-3 border-t border-gray-200 pt-3 text-xs text-gray-500">
            Mémorisé dans Activity sur le sujet{' '}
            <span className="font-semibold text-gray-700">{card.activityLabel}</span>.
          </p>
        )}
      </div>
    </article>
  );
};
